import { ClientObject, ClientObjectProperty, PropertyInit } from '../common/clientObject';
import { RequestType, ValueType } from '../comObject';
import { isNewDocument } from '../common/officeDocumentUtils';
import { SaveFormat } from './saveFormat.enum';
import { Window } from './window';
import { SaveOptions } from './saveOptions.enum';

export class Document extends ClientObject implements PropertyInit {
    private properties = this.clientObjectInitPropsInfo({
        fullName: { propertyName: 'fullName', name: 'FullName' },
        saved: { propertyName: 'saved', name: 'Saved' },
        name: { propertyName: 'name', name: 'Name' },
        readOnly: { propertyName: 'readOnly', name: 'ReadOnly' },
        normalizedFullName: { propertyName: 'normalizedFullName', name: 'NormalizedFullName', extension: true },
    });

    public getPropertyInitializers(): Map<keyof Document, () => void> {
        return new Map<keyof Document, () => void>(
            // FIXME: explicit value argument type should not be required
            Object.values(this.properties).map((value: ClientObjectProperty<Document>) => [
                value.propertyName,
                (): void =>
                    this.reference.request({
                        type: RequestType.GetProperty,
                        cacheName: value.propertyName,
                        name: value.name,
                        extension: value.extension,
                    }),
            ]),
        ).set('isNew', () => {
            this.reference.request({
                type: RequestType.GetProperty,
                cacheName: 'normalizedFullName',
                name: 'NormalizedFullName',
                extension: true,
            });
        });
    }

    public get activeWindow(): Window {
        return this.reference.requestObject({
            type: RequestType.GetProperty,
            creator: Window,
            name: 'ActiveWindow',
        }).value;
    }

    public get fullName(): string {
        return this.reference.getValue<string>(this.properties.fullName.propertyName).value;
    }

    /**
     * Extension to {@link fullName}.
     *
     * If original value represents OneDrive url, attempts to convert it to actual path on disk and returns the latter.
     * Returns unmodified value otherwise.
     */
    public get normalizedFullName(): string {
        return this.reference.getValue<string>(this.properties.normalizedFullName.propertyName).value;
    }

    /**
     * https://docs.microsoft.com/en-us/office/vba/api/word.document.close(method)
     * https://docs.microsoft.com/en-us/dotnet/api/microsoft.office.interop.word._document.close?view=word-pia
     */
    public close(saveOptions?: SaveOptions): void {
        return this.reference.request({
            type: RequestType.Invoke,
            name: 'Close',
            args: saveOptions === undefined ? undefined : [{ value: saveOptions, type: ValueType.Integer }],
        });
    }

    public get saved(): boolean {
        return this.reference.getValue<boolean>(this.properties.saved.propertyName).value;
    }

    public get name(): string {
        return this.reference.getValue<string>(this.properties.name.propertyName).value;
    }

    public get readOnly(): boolean {
        return this.reference.getValue<boolean>(this.properties.readOnly.propertyName).value;
    }

    public save(): void {
        return this.reference.request({
            type: RequestType.Invoke,
            name: 'Save',
        });
    }

    public saveAs2(filePath: string): void {
        return this.reference.request<void>({
            type: RequestType.Invoke,
            name: 'SaveAs2',
            args: [
                { value: filePath, type: ValueType.String },
                { value: SaveFormat.DocumentDefault, type: ValueType.Integer },
            ],
        });
    }

    public get isNew(): boolean {
        return isNewDocument(this.reference.getValue<string>(this.properties.normalizedFullName.propertyName).value);
    }

    public activate(): void {
        return this.reference.request({
            type: RequestType.Invoke,
            name: 'Activate',
        });
    }
}
